// @ts-strict-ignore
import React, { memo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { actionAtom } from 'atoms/atoms'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Box, Button, Icon, toast } from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { Approval, DurationInMilliseconds } from 'constants/General'
import {
  useCreateMassFiatPayoutsPostRequest,
  useCreateMassFiatPayoutsPreRequest,
} from 'hooks/mutations/useCreateMassFiatPayoutsMutation'
import { usePutSubmitCryptoMassPayoutMutation } from 'hooks/mutations/usePutSubmitCryptoMassPayoutMutation'
import { useApprovalsConfigForFlow } from 'hooks/useApprovalsConfigForFlow'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ActionIds, WalletTypes } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import { logSentryError } from 'utils/sentry'
import track from 'utils/tracker'
import {
  authRequestAtom,
  fiatBatchReferenceAtom,
  isConfirmBatchModalVisibleAtom,
  massPayoutStepAtom,
  modalAtom,
  totalNumberOfPayoutsAtom,
  walletTypeAtom,
} from '../atoms/massPayoutAtoms'
import { useIsCryptoBatch } from '../hooks/useIsCryptoBatch'
import { MassPayoutsUpload } from '../pages/MassPayoutsUpload'
import {
  BatchStatus,
  BatchSummaryInformation,
  MassPayoutButtonGroup,
  MassPayoutButtonGroupType,
  MassPayoutPostAuthRequest,
  MassPayoutSteps,
  ModalVariant,
} from '../types'
import { MassPayoutConfirmationModal } from './MassPayoutConfirmationModal'

export const MassPayoutsTemplates = {
  FIAT_B2B_B2C_AND_CRYPTO_TEMPLATES_ZIP_V3: `${process.env.ASSETS_URL}/templates/v3/b2c/Mass Payouts V2 Templates.zip`,
  FIAT_B2B_B2C_AND_CRYPTO_TEMPLATES_ZIP_V4: `${process.env.ASSETS_URL}/templates/v4/Mass Payouts V4 Templates.zip`,
} as const

const SubmitDisableStatuses: string[] = [
  BatchStatus.PROCESSING,
  BatchStatus.CREATED,
  BatchStatus.COMPLETED,
]

interface MassPayoutHeaderProps {
  batchInformation?: BatchSummaryInformation
  buttonGroup: MassPayoutButtonGroupType
}

const MassPayoutHeader = ({ batchInformation, buttonGroup }: MassPayoutHeaderProps) => {
  const { t } = useTranslation()
  const { reference: batchId } = useParams()
  const [isOpen, setIsOpen] = useState(false)
  const permissions = useMappedRoles()
  const canEdit = permissions?.massPayouts?.canEdit
  const queryClient = useQueryClient()
  const setMassPayoutStep = useSetAtom(massPayoutStepAtom)
  const fiatBatchReference = useAtomValue(fiatBatchReferenceAtom)
  const [isConfirmBatchModalVisible, setIsConfirmBatchModalVisible] = useAtom(
    isConfirmBatchModalVisibleAtom
  )
  const setModal = useSetAtom(modalAtom)
  const setAction = useSetAtom(actionAtom)
  const [authRequest, setAuthRequest] = useAtom<MassPayoutPostAuthRequest>(authRequestAtom)
  const handleDownloadCSVTemplate = () => {
    window.open(MassPayoutsTemplates.FIAT_B2B_B2C_AND_CRYPTO_TEMPLATES_ZIP_V4, '_blank')
  }
  const walletType = useAtomValue(walletTypeAtom)
  const setNumberOfPayouts = useSetAtom(totalNumberOfPayoutsAtom)

  const isCrypto = useIsCryptoBatch(walletType)

  const { approvalsIsRequiredForFlow } = useApprovalsConfigForFlow(
    Approval.FLOWS.FIAT_3RD_PARTY_PAYOUT
  )

  const { mutate: createMassFiatPayoutPreRequest } = useCreateMassFiatPayoutsPreRequest()

  const { mutate: createMassFiatPayoutPostRequest } = useCreateMassFiatPayoutsPostRequest()

  const handleCreateMassFiatPayout = () => {
    createMassFiatPayoutPreRequest(fiatBatchReference || batchInformation?.reference, {
      onError: (error: BaseErrorResponse) => {
        if (error?.status === 428) {
          setAuthRequest({
            ...authRequest,
            challenge: error?.data?.challenge?.value,
            callbackUrl: error?.data?.callbackUrl,
          })
          setIsConfirmBatchModalVisible(true)
        }
        if (error.status === 400) {
          // Display appropriate callout with error message sent from the BE.
        } else {
          // Display generic/fallback error messsge
        }
      },
    })
  }

  const onSubmitPostRequest = (password: string) => {
    track.Amp.track(AmplitudeEvent.MASS_PAYOUTS_CONFIRM, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
    setIsConfirmBatchModalVisible(false)

    const payoutsPostRequest = {
      challenge: authRequest?.challenge,
      tokenResponse: password || authRequest?.tokenResponse,
      mfaCallbackUrl: authRequest?.callbackUrl,
    }

    createMassFiatPayoutPostRequest(payoutsPostRequest, {
      onSuccess: async () => {
        track.Amp.track(AmplitudeEvent.EOF_MASS_PAYOUTS_SUCCESS, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
          step: 'batch_submission',
          batchType: WalletTypes.FIAT,
          batchReference: fiatBatchReference,
        })
        if (approvalsIsRequiredForFlow) {
          setMassPayoutStep(MassPayoutSteps.APPROVAL)
        } else {
          setMassPayoutStep(MassPayoutSteps.SUCCESS)
        }
        await queryClient.invalidateQueries({ queryKey: ['massFiatPayoutsDetails'] })
      },
      onError: (error: BaseErrorResponse) => {
        track.Amp.track(AmplitudeEvent.EOF_MASS_PAYOUTS_ERROR, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.ERROR,
        })
        toast({
          header: t('oopsSomethingWentWrong'),
          variant: 'error',
          duration: DurationInMilliseconds.TEN_SECONDS,
        })
        logSentryError('Error: submitFiatMassPayoutPut', error, {
          fiatBatchReference,
        })
      },
    })
  }

  const handleUploadNewFile = () => {
    const isPayoutSubmitted =
      batchInformation?.status !== BatchStatus.VALIDATED &&
      batchInformation?.status !== BatchStatus.CREATED &&
      batchInformation?.status !== BatchStatus.DRAFT
    if (!isPayoutSubmitted) {
      setModal({ isOpen: true, variant: ModalVariant.DRAFT })
    } else {
      track.Amp.track(AmplitudeEvent.MASS_PAYOUTS_INITIATE, {
        category: AmplitudeEventCategory.MERCHANT_PORTAL,
        action: AmplitudeEventAction.CLICK,
      })
      setIsOpen(true)
      setAction({
        actionId: ActionIds.MASSPAYOUTS_UPLOAD,
        header: t('massPayouts.createNewMassPayout'),
        onClose: () => {
          setIsOpen(false)
          setMassPayoutStep(MassPayoutSteps.FORM)
        },
      })
    }
  }

  const { mutate: submitCryptoMassPayoutPut } = usePutSubmitCryptoMassPayoutMutation()

  const handleConfirmCryptoBatch = () => {
    setIsConfirmBatchModalVisible(true)
  }

  const handleSubmitCryptoBatch = () => {
    track.Amp.track(AmplitudeEvent.MASS_PAYOUTS_CONFIRM, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
    submitCryptoMassPayoutPut(
      { batchId },
      {
        onSuccess: async response => {
          track.Amp.track(AmplitudeEvent.EOF_MASS_PAYOUTS_SUCCESS, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.CLICK,
            step: 'batch_submission',
            batchType: WalletTypes.CRYPTO,
            batchReference: batchId,
          })
          setNumberOfPayouts(response?.totalItems - response?.totalProcessedItems)
          setIsConfirmBatchModalVisible(false)
          setMassPayoutStep(MassPayoutSteps.SUCCESS)
          await queryClient.invalidateQueries({ queryKey: reactQueryKeys.massCryptoPayoutsList() })
        },
        onError: (error: BaseErrorResponse) => {
          track.Amp.track(AmplitudeEvent.EOF_MASS_PAYOUTS_ERROR, {
            category: AmplitudeEventCategory.MERCHANT_PORTAL,
            action: AmplitudeEventAction.ERROR,
          })
          toast({
            header: t('oopsSomethingWentWrong'),
            variant: 'error',
            duration: DurationInMilliseconds.TEN_SECONDS,
          })
          logSentryError('Error: submitCryptoMassPayoutPut', error, {
            batchId,
          })
        },
      }
    )
  }

  const isFiatBatchValid =
    (!isCrypto && Number(batchInformation?.numberOfErrors) === 0) ||
    !batchInformation?.numberOfErrors
  const isCryptoBatchValid =
    isCrypto &&
    batchInformation?.status === BatchStatus.DRAFT &&
    Number(batchInformation?.numberOfErrors) === 0
  const isBatchValidated = isCrypto ? isCryptoBatchValid : isFiatBatchValid
  const isSubmitDisabled =
    SubmitDisableStatuses.includes(batchInformation?.status) || !isBatchValidated || !canEdit

  const HeaderButtons = () => {
    if (buttonGroup === MassPayoutButtonGroup?.BATCH_LIST) {
      return (
        <>
          <Button
            iconElement={<Icon name="DownloadSimple" />}
            onClick={handleDownloadCSVTemplate}
            disabled={!canEdit}
            testId="download-masspayout-templates-btn"
            variant="secondary"
          >
            {t('massPayouts.downloadTemplates')}
          </Button>
          <Button
            iconElement={<Icon name="Plus" />}
            onClick={handleUploadNewFile}
            testId="submit-masspayout-templates-btn"
            disabled={!canEdit}
          >
            {t('massPayouts.createNewMassPayout')}
          </Button>
        </>
      )
    }

    if (buttonGroup === MassPayoutButtonGroup?.BATCH_DETAILS) {
      return (
        <>
          <Button
            iconElement={<Icon name="UploadSimple" />}
            onClick={handleUploadNewFile}
            disabled={!canEdit}
            testId="upload-masspayout-btn"
            variant="secondary"
          >
            {t('massPayouts.uploadNew')}
          </Button>
          <Button
            iconElement={<Icon name="CreditCard" />}
            onClick={isCrypto ? handleConfirmCryptoBatch : handleCreateMassFiatPayout}
            testId="submit-masspayout-templates-btn"
            disabled={isSubmitDisabled}
          >
            {t('massPayouts.submitPayments')}
          </Button>
        </>
      )
    }

    if (buttonGroup === MassPayoutButtonGroup?.BATCH_UPLOAD) {
      return (
        <Button
          iconElement={<Icon name="DownloadSimple" />}
          onClick={handleDownloadCSVTemplate}
          disabled={!canEdit}
          testId="download-masspayout-templates-btn"
        >
          {t('massPayouts.downloadTemplates')}
        </Button>
      )
    }
  }
  return (
    <>
      <PageHeader
        description={t('massPayouts.pageHeader')}
        renderRightSection={
          <Box flex justify="end" gapX={12}>
            <HeaderButtons />
          </Box>
        }
      />
      {isConfirmBatchModalVisible && (
        <MassPayoutConfirmationModal
          onContinue={isCrypto ? handleSubmitCryptoBatch : onSubmitPostRequest}
          onClose={() => setIsConfirmBatchModalVisible(false)}
          batchInformation={batchInformation}
          isLoading={false}
          error={false}
          twoFaRequired={!isCrypto}
        />
      )}

      <MassPayoutsUpload isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  )
}

export default memo(MassPayoutHeader)
